import React, { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { ukTime, ukWeekday } from "@/lib/time";
import { CrmShell } from "@/components/portal/crm/CrmShell";
import ChatDock from "@/components/hub/ChatDock";
import { AlertBell } from "@/components/hub/DealAlerts";

/**
 * HubLayout — the desk's own workspace on the same v3 CRM shell as both partner portals:
 * navy command rail with the organisation card, grouped navigation with live counts, the
 * sticky breadcrumb bar, and a bottom tab row on a phone.
 */
export default function HubLayout() {
  const { pathname } = useLocation();
  const [chatUnread, setChatUnread] = useState(0);
  const [customerApps, setCustomerApps] = useState(0);
  const [enquiries, setEnquiries] = useState(0);
  const [stalled, setStalled] = useState(0);
  const [feedback, setFeedback] = useState(0);
  const [chases, setChases] = useState(0);
  const [clock, setClock] = useState(() => new Date());

  useEffect(() => {
    const t = setInterval(() => setClock(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let alive = true;
    const pull = async () => {
      try {
        const r = await api.get("/hub/customer-applications?status=pending&limit=1");
        if (alive) setCustomerApps(r.data.pending || 0);
      } catch { /* silent */ }
      try {
        const r = await api.get("/hub/network-enquiries?status=new&limit=1");
        if (alive) setEnquiries(r.data.new_count || 0);
      } catch { /* silent */ }
      try {
        const r = await api.get("/hub/stalled-deals");
        if (alive) setStalled((r.data.items || []).length);
      } catch { /* silent */ }
      try {
        const r = await api.get("/hub/chat/unread");
        if (alive) setChatUnread(r.data.unread || 0);
      } catch { /* silent */ }
      try {
        const r = await api.get("/hub/feedback/count");
        if (alive) setFeedback(r.data.open || 0);
      } catch { /* silent */ }
      try {
        const r = await api.get("/hub/client-chases");
        if (alive) setChases(r.data.total || 0);
      } catch { /* silent */ }
    };
    pull();
    const id = setInterval(pull, 25000);
    window.addEventListener("customer-apps:changed", pull);
    window.addEventListener("network-enquiries:changed", pull);
    window.addEventListener("client-chases:changed", pull);
    return () => {
      alive = false;
      clearInterval(id);
      window.removeEventListener("customer-apps:changed", pull);
      window.removeEventListener("network-enquiries:changed", pull);
      window.removeEventListener("client-chases:changed", pull);
    };
  }, []);

  const groups = [
    { title: "Work", items: [
      { to: "/hub", label: "The desk", icon: "grid", end: true, testId: "hub-nav-overview" },
      { to: "/hub/pipeline", label: "Pipeline", icon: "pipeline", testId: "hub-nav-pipeline" },
      { to: "/hub/leads", label: "Leads", icon: "list", testId: "hub-nav-leads" },
      { to: "/hub/messages", label: "Messages", icon: "mail", count: chatUnread, testId: "hub-nav-messages" },
      { to: "/hub/desk-tools", label: "Desk tools", icon: "bolt", count: chases, testId: "hub-nav-desk-tools" },
      { to: "/hub/withdrawn", label: "Put cases back", icon: "back", testId: "hub-nav-withdrawn" },
      { to: "/hub/partner-phones", label: "Partner mobiles", icon: "people", testId: "hub-nav-partner-phones" },
      { to: "/hub/connie", label: "Connie", icon: "sparkle", testId: "hub-nav-connie" },
    ] },
    { title: "Network", items: [
      { to: "/hub/lenders", label: "Lenders", icon: "people", testId: "hub-nav-lenders" },
      { to: "/hub/referrers", label: "Introducers", icon: "user", testId: "hub-nav-referrers" },
      { to: "/hub/applications", label: "Applications", icon: "approve", testId: "hub-nav-applications" },
      { to: "/hub/customer-apps", label: "Customer apps", icon: "inbox", count: customerApps, testId: "hub-nav-customer-apps" },
      { to: "/hub/enquiries", label: "Enquiries", icon: "case", testId: "hub-nav-enquiries", count: enquiries },
      { to: "/hub/feedback", label: "Partner feedback", icon: "idea", count: feedback, testId: "hub-nav-feedback" },
    ] },
    { title: "Setup", items: [
      { to: "/hub/agreements", label: "Agreements", icon: "doc", testId: "hub-nav-agreements" },
      { to: "/hub/rules", label: "Auto-route", icon: "bolt", testId: "hub-nav-auto-route" },
      { to: "/hub/alerts-settings", label: "What reaches you", icon: "bell", testId: "hub-nav-what-reaches-you" },
      { to: "/hub/site-effects", label: "Website look", icon: "star", testId: "hub-nav-website-look" },
      { to: "/hub/bin", label: "Bin", icon: "bin", testId: "hub-nav-bin" },
    ] },
  ];

  const tabs = [
    { to: "/hub", label: "Desk", icon: "grid", end: true },
    { to: "/hub/leads", label: "Leads", icon: "list" },
    { to: "/hub/pipeline", label: "Pipeline", icon: "pipeline" },
    { to: "/hub/messages", label: "Inbox", icon: "mail" },
  ];

  return (
    <>
      <CrmShell
        kind="hub"
        testId="hub-layout"
        railLabel="Lending desk control"
        crumbRoot="Hub"
        orgName="Connexis Group"
        orgSub="The desk"
        pills={[{ label: "Live", tone: "good" }, { label: "Admin" }]}
        groups={groups}
        tabs={tabs}
        wide
        topRight={
          <>
            <span className={`st ${stalled > 0 ? "st-sent" : "st-off"}`} data-testid="hub-topbar-attention">
              <i className="sq" />
              {stalled > 0 ? `${stalled} awaiting a lender` : "every case is moving"}
            </span>
            <AlertBell />
            <a className="btn btn-ghost btn-sm" href="/choose?pick=1" data-testid="hub-switch-area">Switch area</a>
            <span className="num" style={{ fontSize: 12, fontWeight: 600, color: "var(--mut-2)" }} data-testid="hub-topbar-clock">
              {ukWeekday(clock)}<span style={{ margin: "0 6px", color: "var(--line-2)" }}>·</span>{ukTime(clock)} UK
            </span>
          </>
        }
      >
        <Outlet />
      </CrmShell>
      {!pathname.startsWith("/hub/messages") && <ChatDock />}
    </>
  );
}
